import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { ItsOneViewReceiptRow, ItsOneViewReceiptSource } from "@/types/its-one-view";
import { openItsOneViewLink } from "@/utils/its-one-view-links";
import { formatInr, formatShortDate } from "../format-inr";

const SOURCE_LABELS: Record<ItsOneViewReceiptSource, string> = {
  fmb: "FMB",
  sabil: "Sabil",
  yearlyNiyaaz: "Yearly Niyaaz",
  project: "Project",
  shehrullah: "Events",
  lagat: "Lagat",
  hallRent: "Hall rent",
  hallDeposit: "Hall deposit",
};

type ReceiptsPanelProps = {
  items: ItsOneViewReceiptRow[];
  accessibleSources: ItsOneViewReceiptSource[];
};

export default function ReceiptsPanel({ items, accessibleSources }: ReceiptsPanelProps) {
  const [filter, setFilter] = useState<ItsOneViewReceiptSource | "all">("all");

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((r) => r.source === filter);
  }, [items, filter]);

  if (accessibleSources.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        You do not have permission to view any receipt types.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
        <Chip
          label="All"
          size="small"
          color={filter === "all" ? "primary" : "default"}
          onClick={() => setFilter("all")}
          variant={filter === "all" ? "filled" : "outlined"}
        />
        {accessibleSources.map((src) => (
          <Chip
            key={src}
            label={SOURCE_LABELS[src]}
            size="small"
            color={filter === src ? "primary" : "default"}
            onClick={() => setFilter(src)}
            variant={filter === src ? "filled" : "outlined"}
          />
        ))}
      </Box>

      {filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No receipts found for this filter.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>ITS</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={`${row.source}-${row.id}`} hover>
                  <TableCell>{formatShortDate(row.receiptDate)}</TableCell>
                  <TableCell>
                    <Chip label={SOURCE_LABELS[row.source]} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.receiptNo ?? "—"}
                      {row.paymentMode ? ` · ${row.paymentMode}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.itsNo}</TableCell>
                  <TableCell align="right">{formatInr(row.amount)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => openItsOneViewLink(row.link)}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
